import React from "react";
import { MarketplaceStrategy } from "@/types/strategy-types";
import StrategyCard from "./StrategyCard";
import { Skeleton } from "@/components/ui/skeleton";

interface StrategiesListProps {
  strategies: MarketplaceStrategy[];
  isLoading: boolean;
  onSelectStrategy: (strategy: string) => void;
  getRiskLabel?: (level: number) => { text: string; color: string };
  formatDate?: (dateString: string) => string;
  getExchangeLogo?: (exchange: string) => string;
  accountPerformanceData?: any;
  isExchangeBlocked?: (exchange: string) => boolean;
}

const StrategiesSkeletonCard = () => (
  <div className="bg-background border rounded-lg p-4 shadow-sm">
    <div className="space-y-3">
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <div className="grid grid-cols-2 gap-2 pt-2">
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
      <div className="pt-2">
        <Skeleton className="h-9 w-full" />
      </div>
    </div>
  </div>
);

const StrategiesList = ({ 
  strategies, 
  isLoading, 
  onSelectStrategy, 
  getRiskLabel, 
  formatDate, 
  getExchangeLogo, 
  accountPerformanceData,
  isExchangeBlocked
}: StrategiesListProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-wrap justify-center gap-4 md:gap-6 w-full">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1rem)] flex justify-center">
            <div className="w-full max-w-sm">
              <StrategiesSkeletonCard />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (strategies.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold mb-2">No strategies found</h3>
        <p className="text-muted-foreground">
          Try adjusting your filter criteria to see more strategies.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap justify-center gap-4 md:gap-6 w-full">
      {strategies.map((strategy) => (
        <div key={strategy.id} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1rem)] flex justify-center">
          <StrategyCard
            key={strategy.id}
            strategy={strategy}
            onSelect={onSelectStrategy}
            getRiskLabel={getRiskLabel}
            formatDate={formatDate}
            getExchangeLogo={getExchangeLogo}
            performanceData={accountPerformanceData?.[strategy.id]}
            isBlockedInCountry={isExchangeBlocked?.(strategy.exchange)}
          />
        </div>
      ))}
    </div>
  );
};

export default StrategiesList;
